import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Truck, RefreshCw, X } from "lucide-react";
import AdminLayout from "@/admin/AdminLayout";
import { adminApi } from "@/admin/AdminAuth";
import { formatGBP } from "@/lib/formatGBP";
import { toast } from "sonner";

const STATUS_COLORS = {
  paid: "bg-cyan-soft text-navy",
  dispatched: "bg-navy text-white",
  pending: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-700",
  cancelled: "bg-silver/40 text-ink/70",
};
const CARRIERS = ["Royal Mail", "DPD", "Evri", "Other"];

export default function AdminOrderDetail() {
  const { order_number } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get(`/admin/orders/${order_number}`);
      setOrder(res.data);
    } finally {
      setLoading(false);
    }
  }, [order_number]);

  useEffect(() => { load(); }, [load]);

  const resendConfirmation = async () => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/orders/${order_number}/resend-confirmation`);
      toast.success("Confirmation email re-sent.");
    } catch {
      toast.error("Couldn't resend.");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !order) {
    return (
      <AdminLayout title="Order">
        <div className="rounded-2xl bg-white border border-silver/40 shadow-card p-10 flex items-center text-muted text-sm">
          <Loader2 size={16} className="animate-spin mr-2" /> Loading…
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={`Order ${order.order_number}`}>
      <Link
        to="/admin/orders"
        className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-navy mb-5"
        data-testid="admin-order-back"
      >
        <ArrowLeft size={14} /> All orders
      </Link>

      <div className="flex flex-wrap gap-3 items-center mb-6">
        <span
          className={`inline-block rounded-full px-3 py-1 text-xs font-semibold capitalize ${STATUS_COLORS[order.status] || "bg-silver/40"}`}
          data-testid="admin-order-status"
        >
          {order.status}
        </span>
        {order.paid_at && (
          <span className="text-xs text-muted">Paid {order.paid_at.slice(0, 19).replace("T", " ")} UTC</span>
        )}
        {order.dispatched_at && (
          <span className="text-xs text-muted">· Dispatched {order.dispatched_at.slice(0, 19).replace("T", " ")} UTC</span>
        )}
        <span className="ml-auto text-2xl font-bold text-navy tabular-nums">{formatGBP(order.amount_pennies)}</span>
      </div>

      <div className="grid lg:grid-cols-[2fr,1fr] gap-6">
        <div className="space-y-6">
          {/* Items */}
          <Card title="Items">
            <table className="w-full text-sm">
              <thead className="text-muted text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left py-2">Item</th>
                  <th className="text-right py-2">Qty</th>
                  <th className="text-right py-2">Unit</th>
                  <th className="text-right py-2">Line total</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((it, i) => (
                  <tr key={i} className="border-t border-silver/30">
                    <td className="py-3">
                      <div className="flex items-center gap-3">
                        {it.image && (
                          <div className="w-10 h-10 rounded-lg bg-ice-light overflow-hidden border border-silver/40 shrink-0">
                            <img src={it.image} alt="" className="w-full h-full object-cover" />
                          </div>
                        )}
                        <span className="font-medium text-navy">{it.name}</span>
                      </div>
                    </td>
                    <td className="text-right">{it.quantity}</td>
                    <td className="text-right">{formatGBP(it.price_pennies)}</td>
                    <td className="text-right font-semibold">{formatGBP(it.price_pennies * it.quantity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* Customer */}
          <Card title="Customer">
            <dl className="grid sm:grid-cols-2 gap-3 text-sm">
              <Pair label="Name" value={`${order.customer.first_name} ${order.customer.last_name}`} />
              <Pair label="Email" value={<a href={`mailto:${order.customer.email}`} className="text-navy hover:text-cyan">{order.customer.email}</a>} />
              <Pair label="Phone" value={order.customer.phone || "—"} />
            </dl>
          </Card>

          {/* Addresses */}
          <Card title="Addresses">
            <div className="grid sm:grid-cols-2 gap-6 text-sm">
              <AddressBlock label="Shipping" addr={order.shipping_address} />
              <AddressBlock label="Billing" addr={order.billing_address} />
            </div>
          </Card>

          {/* Timeline */}
          <Card title="Timeline">
            <Timeline order={order} />
          </Card>
        </div>

        {/* Action rail */}
        <aside className="lg:sticky lg:top-6 self-start space-y-4">
          {order.status === "paid" && (
            <div className="rounded-2xl bg-white border border-silver/40 shadow-card p-5">
              <h3 className="text-sm font-bold text-navy mb-3">Fulfilment</h3>
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                data-testid="admin-mark-dispatched"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white h-11 text-sm font-semibold hover:brightness-110 transition shadow-card"
              >
                <Truck size={14} /> Mark as dispatched
              </button>
            </div>
          )}
          {order.status === "dispatched" && (
            <div className="rounded-2xl bg-white border border-silver/40 shadow-card p-5">
              <h3 className="text-sm font-bold text-navy mb-2">Dispatched</h3>
              <p className="text-sm text-ink/80" data-testid="admin-order-tracking">
                {order.tracking_number ? (
                  <>Tracking: <strong className="text-navy">{order.tracking_number}</strong>{order.carrier ? ` · ${order.carrier}` : ""}</>
                ) : (
                  <>Tracking number not recorded.</>
                )}
              </p>
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="mt-3 text-xs text-navy underline hover:text-cyan"
                data-testid="admin-edit-tracking"
              >
                Edit tracking
              </button>
            </div>
          )}
          <div className="rounded-2xl bg-white border border-silver/40 shadow-card p-5">
            <h3 className="text-sm font-bold text-navy mb-3">Emails</h3>
            <p className="text-xs text-muted mb-3">
              Confirmation sent: {order.emails_sent?.customer_confirmation ? "Yes" : "No"}<br/>
              Admin notified: {order.emails_sent?.admin_notification ? "Yes" : "No"}
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={resendConfirmation}
              data-testid="admin-resend-confirmation"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-navy text-navy h-10 text-sm font-semibold hover:bg-ice transition disabled:opacity-60"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Resend customer confirmation
            </button>
          </div>
        </aside>
      </div>

      {modalOpen && (
        <DispatchModal
          order={order}
          onClose={() => setModalOpen(false)}
          onSaved={async () => { setModalOpen(false); await load(); toast.success("Order updated."); }}
        />
      )}
    </AdminLayout>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl bg-white border border-silver/40 shadow-card p-5">
      <h3 className="text-sm font-bold text-navy mb-4">{title}</h3>
      {children}
    </div>
  );
}
function Pair({ label, value }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-widest text-muted">{label}</dt>
      <dd className="mt-1 text-ink/90">{value}</dd>
    </div>
  );
}
function AddressBlock({ label, addr }) {
  if (!addr) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-muted mb-1.5">{label}</div>
      <div className="text-ink/90 leading-relaxed">
        {addr.line1}<br/>
        {addr.line2 && <>{addr.line2}<br/></>}
        {addr.city}<br/>
        {addr.postcode}<br/>
        {addr.country}
      </div>
    </div>
  );
}
function Timeline({ order }) {
  const events = [
    { label: "Order created", ts: order.created_at },
    order.paid_at && { label: "Payment confirmed", ts: order.paid_at },
    order.dispatched_at && { label: "Dispatched", ts: order.dispatched_at },
  ].filter(Boolean);
  return (
    <ol className="space-y-3 text-sm">
      {events.map((ev, i) => (
        <li key={i} className="flex gap-3 items-start">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-navy text-cyan text-xs font-bold shrink-0">{i + 1}</span>
          <div>
            <div className="font-semibold text-navy">{ev.label}</div>
            <div className="text-xs text-muted">{(ev.ts || "").slice(0, 19).replace("T", " ")} UTC</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

function DispatchModal({ order, onClose, onSaved }) {
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [carrier, setCarrier] = useState(order.carrier || "Royal Mail");
  const [notes, setNotes] = useState(order.dispatch_notes || "");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const verb = order.status === "dispatched" ? "patch" : "post";
      await adminApi[verb](`/admin/orders/${order.order_number}/dispatch`, {
        tracking_number: tracking.trim(),
        carrier,
        notes: notes.trim(),
      });
      await onSaved();
    } catch {
      toast.error("Couldn't save.");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="admin-dispatch-modal">
      <div className="absolute inset-0 bg-navy/40" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-md rounded-2xl bg-white shadow-card-hover border border-silver/40">
        <header className="flex items-center justify-between px-5 py-4 border-b border-silver/40">
          <h3 className="font-bold text-navy">
            {order.status === "dispatched" ? "Edit tracking" : "Mark as dispatched"}
          </h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-muted hover:text-navy">
            <X size={18} />
          </button>
        </header>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-muted mb-2">Tracking number</label>
            <input
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
              data-testid="admin-dispatch-tracking"
              placeholder="e.g. RM123456789GB"
              className="w-full h-11 rounded-xl border border-silver/70 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-cyan"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-muted mb-2">Carrier</label>
            <select
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              data-testid="admin-dispatch-carrier"
              className="w-full h-11 rounded-xl border border-silver/70 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan"
            >
              {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-muted mb-2">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="admin-dispatch-notes"
              rows={3}
              className="w-full rounded-xl border border-silver/70 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan"
            />
          </div>
        </div>
        <footer className="px-5 py-4 border-t border-silver/40 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 h-10 rounded-full text-sm border border-silver/60 text-ink hover:bg-ice">
            Cancel
          </button>
          <button type="submit" disabled={submitting}
            data-testid="admin-dispatch-confirm"
            className="px-5 h-10 rounded-full bg-navy text-white text-sm font-semibold hover:brightness-110 disabled:opacity-60 inline-flex items-center gap-2">
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Confirm
          </button>
        </footer>
      </form>
    </div>
  );
}
